import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Megaphone, Plus, Loader2, Download, Copy, Trash2, Image as ImageIcon,
  Check, Clock, Eye, RefreshCw, Hash, X, Sparkles,
  Facebook, Instagram, Send, AlertCircle, Link2, Unlink, CheckCircle2
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import ShopLayout from '@/components/shop/ShopLayout';
import AutoPostHistory from '@/components/shop/AutoPostHistory';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-600',
  published: 'bg-green-100 text-green-700',
  scheduled: 'bg-blue-100 text-blue-700',
};

const SocialPostsPage = () => {
  const { user, api: authApi } = useAuth();
  const { t } = useLanguage();
  const shopId = user?.shop_id;

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [viewPost, setViewPost] = useState(null);
  const [settings, setSettings] = useState({});
  const [connections, setConnections] = useState({ facebook: null, instagram_via_facebook: false });
  const [showConnect, setShowConnect] = useState(false);
  const [connectForm, setConnectForm] = useState({ page_id: '', page_access_token: '' });
  const [connecting, setConnecting] = useState(false);
  const [publishTarget, setPublishTarget] = useState(null); // post object
  const [publishPlatforms, setPublishPlatforms] = useState({ facebook: true, instagram: false });
  const [publishing, setPublishing] = useState(false);
  const [publishResults, setPublishResults] = useState(null);

  useEffect(() => {
    if (shopId) fetchData();
  }, [shopId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [postsRes, prodsRes, settingsRes, connsRes] = await Promise.all([
        authApi.get(`/shops/${shopId}/social/posts`),
        authApi.get(`/shops/${shopId}/products?limit=200`),
        authApi.get(`/shops/${shopId}/social/settings`),
        authApi.get(`/shops/${shopId}/social/connections`),
      ]);
      setPosts(postsRes.data.posts);
      setProducts(prodsRes.data);
      setSettings(settingsRes.data);
      setConnections(connsRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleConnect = async () => {
    if (!connectForm.page_id || !connectForm.page_access_token) {
      toast.error('Enter both Page ID and Access Token');
      return;
    }
    setConnecting(true);
    try {
      const res = await authApi.post(`/shops/${shopId}/social/connect/facebook`, connectForm);
      toast.success(`Connected: ${res.data.page_name}${res.data.ig_username ? ' (+ IG @' + res.data.ig_username + ')' : ''}`);
      setShowConnect(false);
      setConnectForm({ page_id: '', page_access_token: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connection failed');
    } finally { setConnecting(false); }
  };

  const handleTestConnection = async () => {
    try {
      const res = await authApi.post(`/shops/${shopId}/social/connect/facebook/test`);
      toast.success(`Connection healthy: ${res.data.page_name}${res.data.ig_username ? ' + @' + res.data.ig_username : ''}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connection test failed');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Facebook? You will need to paste your token again to reconnect.')) return;
    try {
      await authApi.delete(`/shops/${shopId}/social/connect/facebook`);
      toast.success('Disconnected');
      fetchData();
    } catch { toast.error('Disconnect failed'); }
  };

  const handlePublish = async () => {
    const platforms = Object.keys(publishPlatforms).filter(k => publishPlatforms[k]);
    if (platforms.length === 0) { toast.error('Select at least one platform'); return; }
    setPublishing(true);
    setPublishResults(null);
    try {
      const res = await authApi.post(`/shops/${shopId}/social/posts/${publishTarget.id}/publish`, { platforms });
      setPublishResults(res.data.results);
      const okCount = Object.values(res.data.results).filter(r => r?.ok).length;
      if (okCount > 0) {
        toast.success(`Published to ${okCount} platform${okCount > 1 ? 's' : ''}`);
        fetchData();
      } else {
        toast.error('Publish failed on all platforms');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Publish failed');
    } finally { setPublishing(false); }
  };

  const handleGenerate = async () => {
    if (selectedProducts.size === 0) { toast.error('Select at least one product'); return; }
    setGenerating(true);
    try {
      const productData = [...selectedProducts].map(id => {
        const p = products.find(x => x.id === id);
        return p ? { name: p.name, price: p.price } : null;
      }).filter(Boolean);

      const res = await authApi.post(`/shops/${shopId}/social/generate`, {
        product_names: productData,
      });

      toast.success('Post generated!');
      setShowCreate(false);
      setSelectedProducts(new Set());
      setViewPost(res.data);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed. Try again.');
    } finally { setGenerating(false); }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await authApi.delete(`/shops/${shopId}/social/posts/${postId}`);
      setPosts(prev => prev.filter(p => p.id !== postId));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  const handleViewPost = async (postId) => {
    try {
      const res = await authApi.get(`/shops/${shopId}/social/posts/${postId}`);
      setViewPost(res.data);
    } catch { toast.error('Failed to load post'); }
  };

  const copyCaption = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Caption copied to clipboard');
  };

  const downloadImage = (postId) => {
    window.open(`${API}/api/shops/${shopId}/social/posts/${postId}/image`, '_blank');
  };

  if (loading) {
    return <ShopLayout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" /></div></ShopLayout>;
  }

  return (
    <ShopLayout>
      <div className="space-y-6" data-testid="social-posts-page">
        {/* Connections Card */}
        <div className="bg-white border-2 border-slate-100 p-5" data-testid="connections-card">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="text-base font-bold text-[#1e3a5f] flex items-center gap-2">
                <Link2 className="w-4 h-4 text-[#22c55e]" /> Platform Connections
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">Connect Facebook to publish to FB Pages + linked Instagram Business accounts</p>
            </div>
            {!connections.facebook && (
              <button onClick={() => setShowConnect(true)} className="vf-btn-primary text-sm flex items-center gap-2" data-testid="connect-fb-btn">
                <Facebook className="w-4 h-4" /> Connect Facebook
              </button>
            )}
          </div>
          {connections.facebook ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
              {/* Permission warning */}
              {connections.facebook.has_publish_permission === false && (
                <div className="md:col-span-2 p-3 bg-yellow-50 border-2 border-yellow-300 rounded-sm" data-testid="permission-warning">
                  <p className="text-sm font-semibold text-yellow-900 mb-1">⚠ Your Facebook token cannot publish posts yet</p>
                  <p className="text-xs text-slate-700 mb-2">Your token connected fine, but it's missing the <strong>"Create Content"</strong> permission. This means publishing will fail. Easy fix:</p>
                  <ol className="text-xs text-slate-700 list-decimal list-inside space-y-0.5 mb-2">
                    <li>Go back to <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Meta Graph API Explorer</a></li>
                    <li>Click <strong>Add a Permission</strong> and tick <code className="bg-white px-1">pages_manage_posts</code> + <code className="bg-white px-1">instagram_content_publish</code></li>
                    <li>Click <strong>Generate Access Token</strong> again, then re-extend to long-lived</li>
                    <li>Disconnect here and reconnect with the new token</li>
                  </ol>
                  <a href="/shop/help" className="text-xs text-[#22c55e] hover:underline font-medium">See the full guide →</a>
                </div>
              )}
              <div className="flex items-center justify-between p-3 border border-slate-200 rounded-sm bg-blue-50/30">
                <div className="flex items-center gap-3">
                  <Facebook className="w-6 h-6 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-[#1e3a5f]">{connections.facebook.page_name || 'Facebook Page'}</p>
                    <p className="text-xs text-slate-500">ID: {connections.facebook.page_id}</p>
                  </div>
                </div>
                <span className="text-xs text-green-700 font-medium flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Active
                </span>
              </div>
              <div className="flex items-center justify-between p-3 border border-slate-200 rounded-sm bg-pink-50/30">
                <div className="flex items-center gap-3">
                  <Instagram className="w-6 h-6 text-pink-600" />
                  <div>
                    {connections.facebook.ig_username ? (
                      <>
                        <p className="text-sm font-medium text-[#1e3a5f]">@{connections.facebook.ig_username}</p>
                        <p className="text-xs text-slate-500">Linked via Facebook Page</p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-slate-400">Not linked</p>
                        <p className="text-xs text-slate-400">Link IG Business in Meta Business Suite</p>
                      </>
                    )}
                  </div>
                </div>
                {connections.facebook.ig_username && (
                  <span className="text-xs text-green-700 font-medium flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Active
                  </span>
                )}
              </div>
              <div className="md:col-span-2 flex gap-2 pt-2">
                <button onClick={handleTestConnection} className="text-xs text-[#1e3a5f] hover:underline flex items-center gap-1" data-testid="test-conn-btn">
                  <RefreshCw className="w-3 h-3" /> Test Connection
                </button>
                <span className="text-slate-300">·</span>
                <button onClick={handleDisconnect} className="text-xs text-red-600 hover:underline flex items-center gap-1" data-testid="disconnect-btn">
                  <Unlink className="w-3 h-3" /> Disconnect
                </button>
                <span className="text-slate-300">·</span>
                <a href="/shop/help" className="text-xs text-slate-500 hover:underline">How to get a Page Access Token</a>
              </div>
            </div>
          ) : (
            <div className="p-4 border border-dashed border-slate-300 rounded-sm bg-slate-50 text-center">
              <p className="text-sm text-slate-600">No platforms connected yet. <a href="/shop/help" className="text-[#22c55e] hover:underline">Read the setup guide</a> or click Connect Facebook above.</p>
            </div>
          )}
        </div>

        {/* Auto-Post History */}
        <div className="bg-white border-2 border-slate-100 p-5" data-testid="auto-post-history-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-base font-bold text-[#1e3a5f] flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-[#22c55e]" /> Recent Auto-Posts
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">The last 5 posts auto-generated from stock intakes</p>
            </div>
            <a href="/shop/settings" className="text-xs text-[#22c55e] hover:underline">Configure automation →</a>
          </div>
          <AutoPostHistory shopId={shopId} compact />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[#1e3a5f]">Social Media Posts</h2>
            <p className="text-slate-500 text-sm">AI-generated promotional content for your shop</p>
          </div>
          <button onClick={() => setShowCreate(true)}
            className="vf-btn-primary flex items-center gap-2"
            data-testid="create-post-btn">
            <Sparkles className="w-4 h-4" /> Generate New Post
          </button>
        </div>

        {/* Posts Grid */}
        {posts.length === 0 ? (
          <div className="bg-white border-2 border-slate-100 p-12 text-center">
            <Megaphone className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <h3 className="text-lg font-bold text-[#1e3a5f] mb-1">No Posts Yet</h3>
            <p className="text-sm text-slate-500 mb-4">Generate your first promotional post to share on social media</p>
            <button onClick={() => setShowCreate(true)} className="vf-btn-primary">
              <Sparkles className="w-4 h-4 inline mr-2" /> Generate Your First Post
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {posts.map(post => (
              <div key={post.id} className="bg-white border-2 border-slate-100 overflow-hidden hover:border-[#1e3a5f]/20 transition-colors"
                data-testid={`post-card-${post.id}`}>
                <div className="aspect-square bg-slate-100 flex items-center justify-center cursor-pointer" onClick={() => handleViewPost(post.id)}>
                  <div className="text-center p-4">
                    <ImageIcon className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p className="text-xs text-slate-400">Click to view</p>
                  </div>
                </div>
                <div className="p-3">
                  <p className="text-sm text-slate-700 line-clamp-2 mb-2">{post.caption}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${STATUS_COLORS[post.status] || STATUS_COLORS.draft}`}>
                        {post.status}
                      </span>
                      <span className="text-xs text-slate-400">{new Date(post.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => handleViewPost(post.id)} className="p-1.5 hover:bg-slate-100 rounded-sm text-slate-400 hover:text-[#1e3a5f]" title="View">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button onClick={() => downloadImage(post.id)} className="p-1.5 hover:bg-blue-50 rounded-sm text-slate-400 hover:text-blue-600" title="Download image">
                        <Download className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(post.id)} className="p-1.5 hover:bg-red-50 rounded-sm text-slate-400 hover:text-red-600" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Post Modal */}
      <AnimatePresence>
        {showCreate && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !generating && setShowCreate(false)}>
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-xl bg-white flex flex-col max-h-[85vh]"
                onClick={e => e.stopPropagation()} data-testid="create-post-modal">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
                  <div>
                    <h3 className="text-lg font-bold text-[#1e3a5f]">Generate Social Post</h3>
                    <p className="text-sm text-slate-500">Select products to feature</p>
                  </div>
                  <button onClick={() => !generating && setShowCreate(false)} className="p-2 hover:bg-slate-100 rounded-sm"><X className="w-5 h-5" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 min-h-0">
                  <div className="space-y-1">
                    {products.slice(0, 50).map(p => (
                      <button key={p.id} onClick={() => {
                        setSelectedProducts(prev => {
                          const next = new Set(prev);
                          next.has(p.id) ? next.delete(p.id) : next.add(p.id);
                          return next;
                        });
                      }}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-sm transition-colors ${
                          selectedProducts.has(p.id) ? 'bg-green-50 border border-[#22c55e]' : 'hover:bg-slate-50 border border-transparent'
                        }`}>
                        <div className={`w-5 h-5 border-2 rounded-sm flex items-center justify-center flex-shrink-0 ${
                          selectedProducts.has(p.id) ? 'bg-[#22c55e] border-[#22c55e]' : 'border-slate-300'
                        }`}>
                          {selectedProducts.has(p.id) && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span className="text-sm font-medium text-[#1e3a5f] flex-1">{p.name}</span>
                        <span className="text-sm text-[#22c55e] font-medium">&euro;{p.price?.toFixed(2)}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="p-4 border-t border-slate-200 flex items-center justify-between flex-shrink-0">
                  <span className="text-sm text-slate-500">{selectedProducts.size} selected</span>
                  <button onClick={handleGenerate} disabled={generating || selectedProducts.size === 0}
                    className="vf-btn-primary flex items-center gap-2 disabled:opacity-50"
                    data-testid="generate-post-btn">
                    {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating (up to 60s)...</> : <><Sparkles className="w-4 h-4" /> Generate Post</>}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* View Post Modal */}
      <AnimatePresence>
        {viewPost && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setViewPost(null)}>
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-2xl bg-white flex flex-col max-h-[90vh]"
                onClick={e => e.stopPropagation()} data-testid="view-post-modal">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
                  <h3 className="text-lg font-bold text-[#1e3a5f]">Social Media Post</h3>
                  <button onClick={() => setViewPost(null)} className="p-2 hover:bg-slate-100 rounded-sm"><X className="w-5 h-5" /></button>
                </div>
                <div className="flex-1 overflow-y-auto min-h-0">
                  {/* Image */}
                  {viewPost.image_base64 && (
                    <div className="bg-slate-900 flex items-center justify-center">
                      <img src={`data:image/png;base64,${viewPost.image_base64}`} alt="Promo" className="max-h-[50vh] w-auto" />
                    </div>
                  )}
                  <div className="p-5 space-y-4">
                    {/* Caption */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-sm font-medium text-[#1e3a5f]">Caption (Facebook/Instagram)</label>
                        <button onClick={() => copyCaption(viewPost.caption)} className="text-xs text-[#22c55e] font-medium flex items-center gap-1">
                          <Copy className="w-3 h-3" /> Copy
                        </button>
                      </div>
                      <p className="text-sm text-slate-700 bg-slate-50 p-3 border border-slate-200 rounded-sm">{viewPost.caption}</p>
                    </div>
                    {/* Short Caption */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-sm font-medium text-[#1e3a5f]">Short Caption (Twitter/X)</label>
                        <button onClick={() => copyCaption(viewPost.short_caption)} className="text-xs text-[#22c55e] font-medium flex items-center gap-1">
                          <Copy className="w-3 h-3" /> Copy
                        </button>
                      </div>
                      <p className="text-sm text-slate-700 bg-slate-50 p-3 border border-slate-200 rounded-sm">{viewPost.short_caption}</p>
                    </div>
                    {/* Hashtags */}
                    {viewPost.hashtags?.length > 0 && (
                      <div>
                        <label className="text-sm font-medium text-[#1e3a5f] mb-1 block">Hashtags</label>
                        <div className="flex flex-wrap gap-2">
                          {viewPost.hashtags.map((tag, i) => (
                            <span key={i} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-sm flex items-center gap-1">
                              <Hash className="w-3 h-3" /> {tag.replace('#', '')}
                            </span>
                          ))}
                        </div>
                        <button onClick={() => copyCaption(viewPost.hashtags.join(' '))} className="text-xs text-[#22c55e] font-medium mt-1 flex items-center gap-1">
                          <Copy className="w-3 h-3" /> Copy all hashtags
                        </button>
                      </div>
                    )}
                    {/* Products */}
                    <div>
                      <label className="text-sm font-medium text-[#1e3a5f] mb-1 block">Featured Products</label>
                      <div className="flex flex-wrap gap-2">
                        {viewPost.products?.map((p, i) => (
                          <span key={i} className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-sm">
                            {p.name} — &euro;{p.price?.toFixed(2)}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-4 border-t border-slate-200 flex gap-3 flex-shrink-0">
                  <button onClick={() => setViewPost(null)} className="vf-btn-secondary flex-1">Close</button>
                  <button onClick={() => downloadImage(viewPost.id)}
                    className="vf-btn-secondary flex-1 flex items-center justify-center gap-2"
                    data-testid="download-post-image">
                    <Download className="w-4 h-4" /> Download
                  </button>
                  <button onClick={() => { setPublishTarget(viewPost); setPublishResults(null); setPublishPlatforms({ facebook: true, instagram: !!connections.facebook?.ig_username }); }}
                    disabled={!connections.facebook}
                    title={!connections.facebook ? 'Connect Facebook first' : ''}
                    className="vf-btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="publish-post-btn">
                    <Send className="w-4 h-4" /> Publish
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      {/* Connect Facebook Modal */}
      <AnimatePresence>
        {showConnect && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !connecting && setShowConnect(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-lg bg-white flex flex-col max-h-[90vh]"
              onClick={e => e.stopPropagation()} data-testid="connect-fb-modal">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                  <Facebook className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-bold text-[#1e3a5f]">Connect Facebook Page</h3>
                </div>
                <button onClick={() => !connecting && setShowConnect(false)} className="p-2 hover:bg-slate-100 rounded-sm"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-4 min-h-0">
                <div className="bg-blue-50 border border-blue-200 p-3 text-xs text-slate-700 rounded-sm">
                  <p className="font-medium text-[#1e3a5f] mb-1">Quick setup:</p>
                  <ol className="list-decimal list-inside space-y-0.5">
                    <li>Go to <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Meta Graph API Explorer</a></li>
                    <li>Click <strong>Get Token</strong> → <strong>Get Page Access Token</strong></li>
                    <li>Grant <code className="bg-white px-1">pages_manage_posts</code> + <code className="bg-white px-1">instagram_content_publish</code></li>
                    <li>Extend to long-lived token (60 days) in <a href="https://developers.facebook.com/tools/debug/accesstoken/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Access Token Debugger</a></li>
                    <li>Copy your Page ID from <strong>About</strong> tab on your Facebook Page</li>
                  </ol>
                  <p className="mt-2"><a href="/shop/help" className="text-[#22c55e] hover:underline font-medium">Full step-by-step guide →</a></p>
                </div>

                <div>
                  <label className="text-sm font-medium text-[#1e3a5f] mb-1 block">Facebook Page ID</label>
                  <input type="text" value={connectForm.page_id}
                    onChange={e => setConnectForm({ ...connectForm, page_id: e.target.value.trim() })}
                    placeholder="e.g. 102345678901234"
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#22c55e] focus:outline-none"
                    data-testid="fb-page-id-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1e3a5f] mb-1 block">Page Access Token (long-lived)</label>
                  <textarea value={connectForm.page_access_token}
                    onChange={e => setConnectForm({ ...connectForm, page_access_token: e.target.value.trim() })}
                    placeholder="EAAB..."
                    rows={4}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-xs font-mono focus:border-[#22c55e] focus:outline-none"
                    data-testid="fb-token-input" />
                  <p className="text-xs text-slate-500 mt-1">Stored encrypted (Fernet AES-128). Never sent to the browser after saving.</p>
                </div>
              </div>
              <div className="p-4 border-t border-slate-200 flex gap-2 flex-shrink-0">
                <button onClick={() => !connecting && setShowConnect(false)} className="vf-btn-secondary flex-1">Cancel</button>
                <button onClick={handleConnect} disabled={connecting} className="vf-btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="connect-confirm-btn">
                  {connecting ? <><Loader2 className="w-4 h-4 animate-spin" /> Validating...</> : <><Link2 className="w-4 h-4" /> Validate & Connect</>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Publish Modal */}
      <AnimatePresence>
        {publishTarget && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={() => !publishing && setPublishTarget(null)}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-md bg-white flex flex-col max-h-[90vh]"
              onClick={e => e.stopPropagation()} data-testid="publish-modal">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
                <h3 className="text-lg font-bold text-[#1e3a5f] flex items-center gap-2"><Send className="w-5 h-5" /> Publish to Social Media</h3>
                <button onClick={() => !publishing && setPublishTarget(null)} className="p-2 hover:bg-slate-100 rounded-sm"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3 min-h-0">
                <p className="text-sm text-slate-600">Select where to publish this post:</p>
                <label className={`flex items-center gap-3 p-3 border-2 rounded-sm cursor-pointer transition-colors ${publishPlatforms.facebook ? 'border-blue-600 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                  <input type="checkbox" checked={publishPlatforms.facebook}
                    onChange={e => setPublishPlatforms({ ...publishPlatforms, facebook: e.target.checked })}
                    data-testid="publish-fb-toggle" />
                  <Facebook className="w-5 h-5 text-blue-600" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#1e3a5f]">Facebook Page</p>
                    <p className="text-xs text-slate-500">{connections.facebook?.page_name || '—'}</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-3 border-2 rounded-sm cursor-pointer transition-colors ${publishPlatforms.instagram ? 'border-pink-600 bg-pink-50' : 'border-slate-200 hover:bg-slate-50'} ${!connections.facebook?.ig_username ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input type="checkbox" checked={publishPlatforms.instagram}
                    disabled={!connections.facebook?.ig_username}
                    onChange={e => setPublishPlatforms({ ...publishPlatforms, instagram: e.target.checked })}
                    data-testid="publish-ig-toggle" />
                  <Instagram className="w-5 h-5 text-pink-600" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#1e3a5f]">Instagram</p>
                    <p className="text-xs text-slate-500">{connections.facebook?.ig_username ? '@' + connections.facebook.ig_username : 'No IG Business account linked'}</p>
                  </div>
                </label>

                {publishResults && (
                  <div className="space-y-2 pt-3 border-t border-slate-200">
                    {Object.entries(publishResults).map(([platform, r]) => (
                      <div key={platform} className={`p-3 border rounded-sm text-sm ${r.ok ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          {r.ok ? <CheckCircle2 className="w-4 h-4 text-green-700" /> : <AlertCircle className="w-4 h-4 text-red-700" />}
                          <span className="font-medium text-[#1e3a5f] capitalize">{platform}</span>
                        </div>
                        {r.ok ? (
                          <a href={r.url || r.permalink} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline break-all">{r.url || r.permalink}</a>
                        ) : (
                          <p className="text-xs text-red-700">{r.error}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-slate-200 flex gap-2 flex-shrink-0">
                <button onClick={() => !publishing && setPublishTarget(null)} className="vf-btn-secondary flex-1">Close</button>
                <button onClick={handlePublish} disabled={publishing} className="vf-btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="publish-confirm-btn">
                  {publishing ? <><Loader2 className="w-4 h-4 animate-spin" /> Publishing...</> : <><Send className="w-4 h-4" /> Publish Now</>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </ShopLayout>
  );
};

export default SocialPostsPage;
